import { Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { LanguageProvider } from '@tink/core/src/components/providers/LanguageProvider/LanguageProvider';
import { ProductType } from '@tink/core/src/types';
import { setScopeTag } from '@tink/core/src/sentry';
import { useMessages } from '../../i18n';
import { VERSIONS } from '../../config';
import { NotFoundPage } from '../screens/NotFoundPage/NotFoundPage';
import { getLanguageCode } from '../../utilities/utils';
import { buildSafeUrl, hasSensitiveParamsInUrl } from '../../utilities/urlParametersValidator';
import {
  LazyPrivacyPolicy,
  LazyPrivacyPolicyMarket,
} from '../../core/screens/Legal/PrivacyPolicy/LazyPrivacyPolicy';
import {
  LazyTermsAndConditions,
  LazyTermsAndConditionsMarket,
} from '../../core/screens/Legal/TermsAndConditions/LazyTermsAndConditions';
import { OverlayProvider } from '../ApplicationProviders/OverlayProvider/OverlayProvider';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { TinkLinkAnalytics } from '../../core/Analytics/tink-link-analytics';
import { AnalyticsProvider } from '../../core/Analytics/AnalyticsProvider';
import { RedirectBackToClient } from '../../core/screens/RedirectResult/RedirectBackToClient';
import { ReferBackToClient } from '../../core/screens/RedirectResult/ReferBackToClient';
import { ApplicationEventsCollector } from '../../core/Analytics/ApplicationEventsCollector';
import { DebuggerInfo } from '../DebuggerInfo/DebuggerInfo';
import { ApplicationErrorBoundary } from '../ApplicationErrorBoundary/ApplicationErrorBoundary';
import { LazyRedirectHandlerDirector } from '../RedirectHandlerDirector/LazyRedirectHandlerDirector';
import { CredentialsErrorBoundary } from '../CredentialsErrorBoundary/CredentialsErrorBoundary';
import { routeBuilder } from '../../routes/routeBuilder';
import { TppToWebResultPage } from '../../core/screens/TppToWebResultPage/TppToWebResultPage';
import { TinkLinkPageTitle } from '../TinkLinkPageTitle/TinkLinkPageTitle';
import { useUrlQuery } from '../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { isIframeMode } from '../../utilities/tink-link';
import { LazyPreview } from '../Preview/LazyPreview';
import { RedirectDetectProvider } from '../RedirectDetectProvider/RedirectDetectProvider';
import { LegalProvider } from '../ApplicationProviders/LegalProvider/LegalProvider';
import { AppWrapper } from './App.styles';
import { LegacyRouter } from './LegacyRouter/LegacyRouter';
import { ProductRouter } from './ProductRouter';

const versionRegex = VERSIONS.join('|');
const productTypesRegex = Object.values(ProductType).join('|');

export const App = withRouter(() => {
  const { isIframe, locale, market, providerName: provider, debug } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const messages = useMessages(languageCode);
  const hasSensitiveParams = hasSensitiveParamsInUrl();

  useEffect(() => {
    setScopeTag('market', market);
    setScopeTag('locale', locale);
    setScopeTag('providerName', provider);
  }, [market, locale, provider]);

  if (hasSensitiveParams) {
    const redirectUri = buildSafeUrl();

    return <Redirect to={redirectUri} />;
  }

  return (
    <LanguageProvider defaultLocale={languageCode} messages={messages}>
      <AnalyticsProvider initialTinklinkAnalytics={new TinkLinkAnalytics()}>
        <Suspense fallback={<LoadingOverlay />}>
          <RedirectDetectProvider>
            <LegalProvider>
              <ApplicationEventsCollector />
              <TinkLinkPageTitle languageCode={languageCode} />

              <AppWrapper isRedirectMode={!isIframeMode(isIframe)}>
                <Switch>
                  <Route exact path="/multiscreen-preview">
                    <LazyPreview />
                  </Route>

                  <Route
                    exact
                    path="/terms-and-conditions/:lang?"
                    component={LazyTermsAndConditions}
                  />
                  <Route
                    exact
                    path="/terms-and-conditions/market/:market/:lang?"
                    component={LazyTermsAndConditionsMarket}
                  />
                  <Route exact path="/privacy-policy/:lang?" component={LazyPrivacyPolicy} />

                  <Route
                    exact
                    path="/privacy-policy/market/:market/:lang?"
                    component={LazyPrivacyPolicyMarket}
                  />
                  <OverlayProvider>
                    <ApplicationErrorBoundary>
                      <CredentialsErrorBoundary>
                        <Switch>
                          <Route
                            exact
                            path={[
                              `/:version(${versionRegex})/account-check/connect/:providerName`,
                              `/:version(${versionRegex})/:productType(${productTypesRegex})`,
                              `/:version(${versionRegex})/account-check/connect`,
                            ]}
                            component={LegacyRouter}
                          />

                          <Route
                            exact
                            path={[
                              '/:version/:productType/:productDimension/redirect-handler',
                              '/:version/:productType/:productDimension/**/redirect-handler',
                            ]}
                            component={LazyRedirectHandlerDirector}
                          />

                          <Route
                            exact
                            path="/result/redirect/:clientAppDeeplink/:clientName"
                            component={RedirectBackToClient}
                          />

                          <Route
                            exact
                            path="/result/refer/:clientName"
                            component={ReferBackToClient}
                          />

                          <Route
                            exact
                            path={routeBuilder.buildTppToWebUrl.path}
                            component={TppToWebResultPage}
                          />

                          <Route
                            path={`/:version(${versionRegex})/:productType(${productTypesRegex})/:productDimension`}
                            component={ProductRouter}
                          />

                          <Route path="" component={NotFoundPage} />
                        </Switch>
                        {debug ? <DebuggerInfo /> : null}
                      </CredentialsErrorBoundary>
                    </ApplicationErrorBoundary>
                  </OverlayProvider>
                </Switch>
              </AppWrapper>
            </LegalProvider>
          </RedirectDetectProvider>
        </Suspense>
      </AnalyticsProvider>
    </LanguageProvider>
  );
});
